/**
* First, we will load vue & pinia and attach them to the window object
* Then load the state machine and mixins
* Then load and use all the individual components
*/

import { createApp } from 'vue';
import { createPinia } from 'pinia';
import mitt from 'mitt';

// import plugins
import GlobalsPlugin from "./plugins/GlobalsPlugin.js";
import TooltipDirective from "./directives/tooltip.js";
import i18n from "./i18n";

//Fusion Components
import FusionMain from '@components/Core/FusionMain.vue';

//Create event bus using mitt (Vue 3 compatible)
const emitter = mitt();
window.event_bus = {
  emit: emitter.emit,
  on: emitter.on,
  off: emitter.off,
  // Provide Vue 2-like API for compatibility
  $emit: emitter.emit,
  $on: emitter.on,
  $off: emitter.off
};

//Main Vue Object
const app = createApp({
  components: {
    //Fusion Components
    'fusion-main': FusionMain,
  }
});

// Connect to standalone Vue DevTools in development only. Opt-in via VITE_DEVTOOLS=true:
// without a standalone devtools server listening on :8098, the socket.io transport retries
// forever (ERR_CONNECTION_REFUSED every few seconds), drowning real console errors.
if (import.meta.env.DEV && import.meta.env.VITE_DEVTOOLS === 'true') {
  import('@vue/devtools').then(({ devtools }) => {
    // Use current hostname so it works with custom dev domains (not just localhost)
    devtools.connect(window.location.hostname, 8098);
    console.log('[DevTools] Standalone Vue DevTools connection initiated on', window.location.hostname + ':8098');
  }).catch((err) => {
    console.warn('[DevTools] Failed to load Vue DevTools:', err);
  });
}

app.config.errorHandler = (err, instance, info) => {
  console.error('Vue Error:', err);
  // [FUS-548] Name only — the instance proxy exposes auth_token / client_jwt_token when expanded.
  console.error('Component:', instance?.$options?.name ?? '<anonymous>');
  console.error('Error Info:', info);
  // Optionally send to error tracking service
};

//Suppress specific Vue warnings
app.config.warnHandler = (msg, instance, trace) => {
  // Ignore FilePond acceptedFileTypes warning
  if (msg.includes('Property "acceptedFileTypes" was accessed during render but is not defined on instance')) {
    return;
  }
  // Log other warnings
  console.warn(`[Vue warn]: ${msg}${trace}`);
};

//Load Globals Plugin
app.use(GlobalsPlugin);

//Load i18n (provides $t in templates and this.$t in methods). The active locale is applied from
//Ux.Language by the store's initLanguage()/applyProfileLanguage() actions, not here.
app.use(i18n);

//Register Tooltip Directive
app.directive('tooltip', TooltipDirective);

//Use Pinia store
app.use(createPinia());

//Mount the app
app.mount('#app');

window.app = app;

//Do a unmount() of the Vue app on page unload so it runs the beforeUnmount() method on individual Vue components 
window.addEventListener('pagehide', () => {
  if (window && window.app)
    window.app.unmount();
});